import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, AuthResponse, LoginCredentials } from '@/types'
import { authService } from '@/services/authService'

interface Department {
  id: string;
  name: string;
  type: string;
  code: string;
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => void
  refreshAuth: () => Promise<void>
  /** Merge latest role/profile from GET /api/auth/me (fixes stale role after admin updates DB). */
  syncUserFromApi: () => Promise<void>
  setUser: (user: User) => void
  clearError: () => void
  setLoading: (loading: boolean) => void
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false, // Always start as false, never persist this
      error: null,

      // Actions
      login: async (credentials: LoginCredentials) => {
        try {
          set({ isLoading: true, error: null })

          console.log('Attempting login for:', credentials.email)

          // Add timeout to prevent hanging
          const loginPromise = authService.login(credentials)
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Login request timed out. Please check your connection.')), 30000)
          )

          const response = await Promise.race([loginPromise, timeoutPromise]) as AuthResponse
          console.log('Login response received:', { success: response?.success, user: response?.user?.email })

          if (!response || !response.user || !response.accessToken) {
            throw new Error('Invalid response from server')
          }

          set({
            user: response.user,
            accessToken: response.accessToken,
            refreshToken: response.refreshToken || null,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })

          console.log('Login successful, user stored in state')
        } catch (error: any) {
          console.error('Login error in store:', error)
          const errorMessage = error.message || error.response?.data?.error || error.response?.data?.message || 'Login failed'
          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false,
            user: null,
            accessToken: null,
            refreshToken: null,
          })
          throw error
        }
      },

      logout: () => {
        authService.logout()
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        })
      },

      refreshAuth: async () => {
        try {
          const { refreshToken } = get()
          if (!refreshToken) {
            throw new Error('No refresh token available')
          }

          const response = await authService.refreshToken(refreshToken)

          set({
            accessToken: response.accessToken,
            // Update refresh token if a new one is provided (token rotation)
            refreshToken: response.refreshToken || get().refreshToken,
            error: null,
          })

          await get().syncUserFromApi()
        } catch (error) {
          // If refresh fails, logout user
          get().logout()
          throw error
        }
      },

      syncUserFromApi: async () => {
        const { accessToken, user: prev } = get()
        if (!accessToken || !prev) return
        try {
          const res = (await authService.getProfile()) as { success?: boolean; user?: User }
          if (!res?.user) return
          const u = res.user
          set({
            user: {
              ...prev,
              ...u,
              role: u.role,
              departmentId: u.departmentId ?? prev.departmentId,
              employee: u.employee ?? prev.employee,
            },
          })
        } catch {
          // Non-fatal: tokens remain valid; UI may stay on cached user until next login
        }
      },

      setUser: (user: User) => {
        set({ user })
      },

      clearError: () => {
        set({ error: null })
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },
    }),
    {
      name: 'auth-storage',
      // Use sessionStorage instead of localStorage for better security
      // Tokens will be cleared when the browser session ends
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          return str ? JSON.parse(str) : null;
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        },
      },
      // @ts-ignore
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        // Explicitly exclude isLoading and error from persistence
        // They should always start fresh on page load
      }) as Partial<AuthState & AuthActions>,
      // Ensure isLoading is always false on hydration
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isLoading = false
          state.error = null
        }
      },
    }
  )
)

// Helper to check if user has required role
export const useHasRole = (requiredRole: string | string[]) => {
  const user = useAuthStore((state) => state.user)

  if (!user) return false

  const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole]

  const roleHierarchy = {
    MESSENGER: 1,
    PERSONEL: 1.5,
    OPERATOR: 2,
    DEPO_KULLANICISI: 2.5,
    DAIRE_BASKANI: 2.8,
    SUPERVISOR: 3,
    GENEL_SEKRETER: 3.5,
    BASKAN_YARDIMCISI: 4,
    BASKAN: 4.5,
    ADMIN: 4.8,
    PRESIDENT: 5,
  }

  const userLevel = roleHierarchy[user.role as keyof typeof roleHierarchy] || 0
  const requiredLevel = Math.min(...roles.map(role => roleHierarchy[role as keyof typeof roleHierarchy] || 0))

  return userLevel >= requiredLevel
}

// Beyaz Masa helpers
export function isDepartmentHead(user: User | null): boolean {
  return user?.role === 'DAIRE_BASKANI';
}

export function isStaff(user: User | null): boolean {
  return user?.role === 'PERSONEL';
}

export function canManageDepartment(user: User | null): boolean {
  return user?.role === 'DAIRE_BASKANI' || user?.role === 'ADMIN';
}

export function canAccessAllTickets(user: User | null): boolean {
  const unrestrictedRoles = ['ADMIN', 'PRESIDENT', 'BASKAN', 'BASKAN_YARDIMCISI', 'GENEL_SEKRETER'];
  return user?.role ? unrestrictedRoles.includes(user.role) : false;
}
